import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { ComparaisonApparts } from "@/components/ComparaisonApparts";
import { useFilters } from "@/context/FilterContext";
import { api, download, errMsg, money } from "@/lib/api";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const COLS = ["Dépôts reçus", "Comm. Airbnb", "Comm. Booking", "TPS", "TVQ", "Nuits", "Occupation"];
const pct = (v) => `${Number(v).toLocaleString("fr-CA", { maximumFractionDigits: 1 })} %`;
const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

const Ligne = ({ label, x, total, testId }) => (
  <tr className={`border-t border-slate-100 ${total ? "bg-slate-50 font-semibold" : "hover:bg-slate-50"}`} data-testid={testId}>
    <td className="py-2 px-3 text-left font-medium">{label}</td>
    {["depots", "commission_airbnb", "commission_booking", "tps", "tvq"].map((k) => <td key={k} className="py-2 px-3 text-right tabular" data-testid={`${testId}-${k}`}>{money(x[k])}</td>)}
    <td className="py-2 px-3 text-right tabular">{x.nuits}</td>
    <td className="py-2 px-3 text-right tabular">
      <span className="inline-flex items-center gap-2"><span className="hidden sm:block h-1.5 w-14 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-emerald-600" style={{ width: `${Math.min(x.taux, 100)}%` }} /></span>{pct(x.taux)}</span>
    </td>
  </tr>
);

const Appart = ({ a }) => (
  <Panel title={a.logement} testId={`appart-${slug(a.logement)}`} action={<span className="text-xs text-slate-500">{a.nb_reservations} réservation(s)</span>}>
    <div className="overflow-x-auto -mx-5">
      <table className="w-full table-fixed text-sm min-w-[860px]">
        <thead><tr><th className="py-2 px-3 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">Mois</th>
          {COLS.map((c) => <th key={c} className="py-2 px-3 text-right text-[11px] font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>
          {a.mois.map((m, i) => <Ligne key={m.mois} label={MOIS[i]} x={m} testId={`appart-${slug(a.logement)}-${m.mois}`} />)}
          <Ligne label="TOTAL" x={a.total} total testId={`appart-${slug(a.logement)}-total`} />
        </tbody>
      </table>
    </div>
  </Panel>
);

export default function Appartements() {
  const { options } = useFilters();
  const years = [...new Set([String(new Date().getFullYear()), ...options.mois.map((m) => m.slice(0, 4))])].sort().reverse();
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [dl, setDl] = useState("");
  useEffect(() => {
    setData(null);
    api.get("/appartements/bilan", { params: { annee } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [annee]);
  const exporter = async (fmt) => {
    setDl(fmt);
    try { await download(`/appartements/bilan/${fmt}`, { annee }, `bilan_appartements_${annee}.${fmt === "excel" ? "xlsx" : "pdf"}`); } catch (e) { toast.error(errMsg(e)); } finally { setDl(""); }
  };
  return (
    <div data-testid="appartements-page">
      <PageHeader eyebrow="Airbnb · Booking" title={`Bilan par appartement ${annee}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="h-9 w-28 bg-white" data-testid="appart-annee-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y} data-testid={`appart-annee-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" disabled={!!dl || !data} onClick={() => exporter("pdf")} data-testid="appart-export-pdf">{dl === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}PDF</Button>
          <Button variant="outline" disabled={!!dl || !data} onClick={() => exporter("excel")} data-testid="appart-export-excel">{dl === "excel" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Excel</Button>
        </div>
      </PageHeader>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <div className="space-y-6">
          <ComparaisonApparts apparts={data.appartements.filter((a) => a.logement !== "Non attribué")} />
          {data.appartements.length ? data.appartements.map((a) => <Appart key={a.logement} a={a} />)
            : <p className="text-sm text-slate-500" data-testid="appart-vide">Aucune réservation Airbnb ou Booking pour {annee}.</p>}
          <p className="text-xs text-slate-500 max-w-4xl">Dépôts reçus = virements Desjardins rapprochés, datés du mois (un dépôt groupé est réparti au prorata des montants versés de ses réservations). Commissions, TPS et TVQ : réservations du mois. Occupation = nuits réservées ÷ jours du mois.</p>
        </div>
      )}
    </div>
  );
}
